// src/pages/ReturnsPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  collection, addDoc, query, where, getDocs,
  doc, getDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useApp } from '../context/AppContext';
import {
  RefreshCw, Package, AlertCircle, CheckCircle, Clock, ArrowLeft,
} from 'lucide-react';
import toast from 'react-hot-toast';

const RETURN_REASONS = [
  'Product damaged / broken',
  'Wrong product received',
  'Product not as described',
  'Missing items in order',
  'Quality not satisfactory',
  'Other',
];

const STATUS_CONFIG = {
  pending:   { label: 'Under Review',    color: 'bg-amber-100 text-amber-700',  Icon: Clock        },
  approved:  { label: 'Approved',        color: 'bg-green-100 text-green-700',  Icon: CheckCircle  },
  rejected:  { label: 'Rejected',        color: 'bg-red-100 text-red-700',      Icon: AlertCircle  },
  refunded:  { label: 'Refund Processed',color: 'bg-blue-100 text-blue-700',    Icon: CheckCircle  },
  pickup_scheduled: { label: 'Pickup Scheduled', color: 'bg-purple-100 text-purple-700', Icon: Package },
};

export default function ReturnsPage() {
  const { user }   = useApp();
  const [orders,   setOrders]   = useState([]);
  const [returns,  setReturns]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [form,     setForm]     = useState({ orderId: '', reason: '', description: '', itemIds: [] });
  const [saving,   setSaving]   = useState(false);
  const [activeTab, setActiveTab] = useState('new');

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    const fetchData = async () => {
      try {
        // Get delivered orders
        const ordersQ  = query(collection(db, 'orders'), where('userId', '==', user.uid));
        const ordersSnap = await getDocs(ordersQ);
        const delivered  = ordersSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((o) => o.status === 'delivered');
        setOrders(delivered);

        // Get existing return requests
        const returnsQ   = query(collection(db, 'returns'), where('userId', '==', user.uid));
        const returnsSnap = await getDocs(returnsQ);
        setReturns(returnsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const selectedOrder = orders.find((o) => o.id === form.orderId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.orderId)     { toast.error('Please select an order'); return; }
    if (!form.reason)      { toast.error('Please select a reason'); return; }
    if (!form.description.trim()) { toast.error('Please describe the issue'); return; }

    // Check if already submitted for this order
    const existing = returns.find((r) => r.orderId === form.orderId);
    if (existing) { toast.error('Return already submitted for this order'); return; }

    setSaving(true);
    try {
      const order = selectedOrder;
      const ref   = await addDoc(collection(db, 'returns'), {
        userId:       user.uid,
        userEmail:    user.email,
        userName:     user.displayName,
        orderId:      form.orderId,
        orderTotal:   order?.total,
        items:        order?.items || [],
        merchantId:   order?.merchantIds?.[0] || null,
        reason:       form.reason,
        description:  form.description.trim(),
        status:       'pending',
        refundAmount: order?.total || 0,
        submittedAt:  serverTimestamp(),
      });

      setReturns((prev) => [...prev, {
        id: ref.id, orderId: form.orderId, reason: form.reason,
        status: 'pending', submittedAt: new Date(),
      }]);
      setForm({ orderId: '', reason: '', description: '', itemIds: [] });
      setActiveTab('history');
      toast.success('Return request submitted! ✅ We will review within 24 hours.');
    } catch (err) {
      console.error(err);
      toast.error('Failed to submit return request');
    } finally {
      setSaving(false);
    }
  };

  if (!user) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-cream pt-20">
      <div className="text-5xl">🔒</div>
      <h2 className="font-display font-bold text-2xl text-gray-800">Please login first</h2>
      <Link to="/login" className="bg-rose-500 text-white px-6 py-3 rounded-2xl hover:bg-rose-600 transition-colors">Login</Link>
    </div>
  );

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center pt-20">
      <div className="w-10 h-10 border-4 border-rose-100 border-t-rose-500 rounded-full animate-spin" />
    </div>
  );

  const inputClass = "w-full px-4 py-3 rounded-2xl border border-gray-200 focus:outline-none focus:border-rose-400 font-body text-gray-800 text-sm bg-gray-50 focus:bg-white transition-all";

  return (
    <div className="min-h-screen bg-cream pt-20 pb-16">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <div className="py-8">
          <Link to="/orders" className="flex items-center gap-1.5 text-gray-400 hover:text-rose-500 text-sm font-body mb-5 transition-colors">
            <ArrowLeft size={16} /> Back to Orders
          </Link>
          <h1 className="font-display font-bold text-3xl text-gray-900 flex items-center gap-3">
            <RefreshCw size={28} className="text-rose-400" /> Returns & Refunds
          </h1>
          <p className="text-gray-400 font-body text-sm mt-2">Return accepted within 7 days of delivery.</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-2xl p-1 shadow-sm border border-gray-100 mb-6">
          {[{ id: 'new', label: 'New Request' }, { id: 'history', label: `My Returns (${returns.length})` }].map(({ id, label }) => (
            <button key={id} onClick={() => setActiveTab(id)}
              className={`flex-1 py-2.5 rounded-xl text-sm font-body font-semibold transition-all ${
                activeTab === id ? 'bg-rose-500 text-white' : 'text-gray-500 hover:text-gray-700'
              }`}>
              {label}
            </button>
          ))}
        </div>

        {/* New Return Request */}
        {activeTab === 'new' && (
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 space-y-5">
            <h2 className="font-display font-semibold text-gray-900 text-lg">Submit Return Request</h2>

            {orders.length === 0 ? (
              <div className="text-center py-10">
                <Package size={40} className="text-gray-200 mx-auto mb-3" />
                <p className="text-gray-400 font-body text-sm">No delivered orders eligible for return.</p>
                <p className="text-gray-300 font-body text-xs mt-1">Only delivered orders can be returned.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Select Order */}
                <div>
                  <label className="block text-sm font-body font-semibold text-gray-700 mb-1.5">
                    Select Order <span className="text-rose-500">*</span>
                  </label>
                  <select value={form.orderId} onChange={(e) => setForm(f => ({ ...f, orderId: e.target.value }))}
                    className={inputClass}>
                    <option value="">Choose an order...</option>
                    {orders.map((o) => (
                      <option key={o.id} value={o.id} disabled={returns.some((r) => r.orderId === o.id)}>
                        #{o.id.slice(-8).toUpperCase()} — ₹{o.total?.toLocaleString('en-IN')}
                        {returns.some((r) => r.orderId === o.id) ? ' (Return submitted)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Show order items if selected */}
                {selectedOrder && (
                  <div className="bg-gray-50 rounded-2xl p-4 space-y-2">
                    {selectedOrder.items?.map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <img src={item.image} alt={item.name} className="w-12 h-12 rounded-xl object-cover shrink-0" />
                        <div>
                          <p className="font-body font-medium text-gray-900 text-sm">{item.name}</p>
                          <p className="text-gray-400 text-xs font-body">Qty: {item.quantity} · ₹{item.price?.toLocaleString('en-IN')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Reason */}
                <div>
                  <label className="block text-sm font-body font-semibold text-gray-700 mb-1.5">
                    Reason for Return <span className="text-rose-500">*</span>
                  </label>
                  <select value={form.reason} onChange={(e) => setForm(f => ({ ...f, reason: e.target.value }))}
                    className={inputClass}>
                    <option value="">Select reason...</option>
                    {RETURN_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-body font-semibold text-gray-700 mb-1.5">
                    Describe the Issue <span className="text-rose-500">*</span>
                  </label>
                  <textarea value={form.description}
                    onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                    rows={4} placeholder="Please describe the issue in detail. Photos help speed up the process..."
                    className={`${inputClass} resize-none`} />
                </div>

                <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 text-xs text-rose-700 font-body leading-relaxed">
                  📋 <strong>Return Policy:</strong> Items must be returned within 7 days of delivery. Personalized items are non-returnable unless damaged. Refund will be credited within 5–7 business days.
                </div>

                <button type="submit" disabled={saving}
                  className="w-full flex items-center justify-center gap-2 bg-rose-500 hover:bg-rose-600 text-white py-4 rounded-2xl font-body font-semibold transition-all disabled:opacity-60">
                  {saving
                    ? <div className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                    : <RefreshCw size={18} />
                  }
                  {saving ? 'Submitting...' : 'Submit Return Request'}
                </button>
              </form>
            )}
          </div>
        )}

        {/* Return History */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            {returns.length === 0 ? (
              <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-12 text-center">
                <RefreshCw size={40} className="text-gray-200 mx-auto mb-3" />
                <p className="text-gray-400 font-body">No return requests yet</p>
              </div>
            ) : (
              returns.map((ret) => {
                const cfg    = STATUS_CONFIG[ret.status] || STATUS_CONFIG.pending;
                const StatusIcon = cfg.Icon;
                const order  = orders.find((o) => o.id === ret.orderId);
                const date   = ret.submittedAt?.toDate?.()?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) || '—';
                return (
                  <div key={ret.id} className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-body font-bold text-gray-900 text-sm">
                          Return #{ret.id.slice(-6).toUpperCase()}
                        </p>
                        <p className="text-gray-400 font-body text-xs">
                          Order #{ret.orderId?.slice(-8).toUpperCase()} · {date}
                        </p>
                      </div>
                      <span className={`flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full ${cfg.color}`}>
                        <StatusIcon size={12} /> {cfg.label}
                      </span>
                    </div>
                    <p className="font-body text-gray-600 text-sm mb-2">
                      <strong>Reason:</strong> {ret.reason}
                    </p>
                    {ret.adminNote && (
                      <div className="bg-gray-50 rounded-xl p-3 mt-2">
                        <p className="text-xs text-gray-400 font-body font-semibold mb-1">Admin Note</p>
                        <p className="font-body text-gray-700 text-sm">{ret.adminNote}</p>
                      </div>
                    )}
                    {ret.status === 'refunded' && ret.refundAmount && (
                      <div className="bg-green-50 border border-green-200 rounded-xl p-3 mt-2">
                        <p className="text-green-700 font-body text-sm font-semibold">
                          ✅ Refund of ₹{ret.refundAmount?.toLocaleString('en-IN')} processed
                        </p>
                        <p className="text-green-600 text-xs font-body mt-0.5">Credit within 5–7 business days</p>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}
